import { useGetAlphaBscPositionsQuery, useGetSettingsQuery } from "@/store/api";
import { Badge, type BadgeTone } from "@/components/ui/badge";

/**
 * AlphaLossCapPill — rolling-24h realized loss vs the alpha-BSC daily
 * loss cap (alpha_bsc_daily_loss_cap_usd, default $30). When the loss
 * reaches the cap, run_tick on the backend refuses to fire — i.e. the
 * engine is paused by the breaker — so the pill goes red. Amber from
 * 70% of cap, subtle (muted) otherwise.
 *
 * Reads only existing endpoints: closed alpha positions + settings.
 */

interface AlphaClosedPosition {
  status?: string;
  exit_ts?: string | null;
  realized_pnl_usd?: number | null;
}

export interface AlphaLossCapState {
  /** Realized PnL over positions exited in the last 24h (signed). */
  pnl24h: number;
  /** abs(loss) — 0 when pnl24h >= 0. */
  loss24h: number;
  capUsd: number;
  capHit: boolean;
  nearCap: boolean;
  isLoading: boolean;
}

/** Tolerant epoch parser — handles both ISO+offset and sqlite
 *  "YYYY-MM-DD HH:MM:SS" (UTC, no timezone marker) timestamps. */
export function utcEpoch(ts: string | null | undefined): number {
  if (!ts) return NaN;
  let v = ts;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(v)) v = v.replace(" ", "T") + "Z";
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/** Shared between the pill and EngineHealthStrip (RTK Query dedupes
 *  the underlying request — same endpoint + args). */
export function useAlphaLossCap(): AlphaLossCapState {
  const closed = useGetAlphaBscPositionsQuery("closed", { pollingInterval: 120000 });
  const settings = useGetSettingsQuery();
  const capUsd = Number(
    (settings.data as Record<string, unknown> | undefined)?.alpha_bsc_daily_loss_cap_usd ?? 30,
  );
  const cutoff = Date.now() - 24 * 3600 * 1000;
  const pnl24h = ((closed.data ?? []) as AlphaClosedPosition[]).reduce((sum, p) => {
    const t = utcEpoch(p.exit_ts);
    return Number.isFinite(t) && t >= cutoff ? sum + Number(p.realized_pnl_usd ?? 0) : sum;
  }, 0);
  const loss24h = pnl24h < 0 ? -pnl24h : 0;
  return {
    pnl24h,
    loss24h,
    capUsd,
    capHit: capUsd > 0 && loss24h >= capUsd,
    nearCap: capUsd > 0 && loss24h >= 0.7 * capUsd,
    isLoading: closed.isLoading || settings.isLoading,
  };
}

export function AlphaLossCapPill({ className }: { className?: string }) {
  const { pnl24h, capUsd, capHit, nearCap, isLoading } = useAlphaLossCap();
  if (isLoading) return null;
  const tone: BadgeTone = capHit ? "bad" : nearCap ? "warn" : "muted";
  const amount = `${pnl24h < 0 ? "−" : "+"}$${Math.abs(pnl24h).toFixed(2)}`;
  return (
    <Badge
      tone={tone}
      className={className}
      title={capHit
        ? "24h realized loss reached the cap — auto-trader refuses new entries until losses roll out of the window"
        : "Rolling-24h realized alpha-BSC PnL vs the daily loss cap"}
    >
      Alpha 24h: {amount} / cap ${capUsd.toFixed(0)}
      {capHit && <span className="ml-1">· paused</span>}
    </Badge>
  );
}
